// Utils: orphanedUserUtils
// Purpose: Detect and repair users present in Firebase Auth but missing
// Firestore profiles ("orphaned"), including name extraction helpers and a
// turnkey workflow for contexts.
/**
 * Orphaned User Detection and Repair Utilities
 * 
 * Handles detection and automatic repair of orphaned users who exist in Firebase Auth
 * but are missing their Firestore profile data.
 */

import { auth } from '../../firebase/firebase';
import { SERVER_URL } from '../../api/core';

/**
 * Detects if a user is orphaned (exists in Firebase Auth but missing Firestore profile)
 * 
 * @param {Object} firebaseUser - Firebase Auth user object
 * @returns {Promise<boolean>} True if user appears to be orphaned
 */
export const detectOrphanedUser = async (firebaseUser) => {
  if (!firebaseUser) {
    return false;
  }

  try {
    console.log('Checking if user is orphaned:', firebaseUser.uid);
    
    // Get a fresh ID token
    const idToken = await firebaseUser.getIdToken(true);
    
    // Try to fetch the user profile
    const response = await fetch(`${SERVER_URL}/auth/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 404) {
      console.log('User profile not found - user appears to be orphaned');
      return true;
    }

    if (!response.ok) {
      console.warn('Error checking user profile, assuming not orphaned:', response.status);
      return false;
    }

    const profileData = await response.json();
    
    // Check if profile data is incomplete (another sign of orphaned state)
    if (!profileData || !profileData.firstName || !profileData.lastName || !profileData.planLevel) {
      console.log('User profile incomplete - user may be orphaned');
      return true;
    }

    console.log('User profile found and complete - not orphaned');
    return false;

  } catch (error) {
    console.error('Error detecting orphaned user:', error);
    // If we can't determine, assume not orphaned to avoid false positives
    return false;
  }
};

/**
 * Repairs an orphaned user by creating their missing Firestore profile
 * 
 * @param {Object} firebaseUser - Firebase Auth user object
 * @param {string} firstName - User's first name
 * @param {string} lastName - User's last name
 * @returns {Promise<Object>} Repaired user profile data
 */
export const repairOrphanedUser = async (firebaseUser, firstName, lastName) => {
  if (!firebaseUser) {
    throw new Error('Firebase user is required for repair');
  }

  if (!firstName || !lastName) {
    throw new Error('First name and last name are required for repair');
  }

  try {
    console.log('Attempting to repair orphaned user:', firebaseUser.uid);
    
    // Get a fresh ID token
    const idToken = await firebaseUser.getIdToken(true);
    
    // Call the repair endpoint
    const response = await fetch(`${SERVER_URL}/auth/repair-orphaned-user`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        firstName: firstName.trim(),
        lastName: lastName.trim()
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to repair orphaned user');
    }

    const repairedData = await response.json();
    console.log('Successfully repaired orphaned user:', repairedData);
    
    return repairedData;

  } catch (error) {
    console.error('Error repairing orphaned user:', error);
    throw error;
  }
};

/**
 * Attempts to extract first and last name from Firebase user data
 * 
 * @param {Object} firebaseUser - Firebase Auth user object
 * @returns {Object} Object with firstName and lastName
 */
export const extractNamesFromFirebaseUser = (firebaseUser) => {
  if (!firebaseUser) {
    return { firstName: '', lastName: '' };
  }

  // Try to get names from displayName
  if (firebaseUser.displayName) {
    const nameParts = firebaseUser.displayName.trim().split(' ');
    if (nameParts.length >= 2) {
      return {
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' ')
      };
    } else if (nameParts.length === 1) {
      return {
        firstName: nameParts[0],
        lastName: 'User' // Default last name
      };
    }
  }

  // Try to extract from email
  if (firebaseUser.email) {
    const emailPart = firebaseUser.email.split('@')[0];
    // Remove numbers and special characters, capitalize
    const cleanName = emailPart.replace(/[^a-zA-Z]/g, '');
    if (cleanName.length > 0) {
      return {
        firstName: cleanName.charAt(0).toUpperCase() + cleanName.slice(1),
        lastName: 'User'
      };
    }
  }

  // Fallback
  return {
    firstName: 'User',
    lastName: 'Account'
  };
};

/**
 * Comprehensive orphaned user detection and repair workflow
 * 
 * @param {Object} firebaseUser - Firebase Auth user object
 * @param {Function} setUser - Function to update user state
 * @param {Object} options - Options for repair process
 * @returns {Promise<Object|null>} Repaired user data or null if not orphaned
 */
export const detectAndRepairOrphanedUser = async (firebaseUser, setUser, options = {}) => {
  const { 
    promptForNames = true, 
    defaultFirstName = null, 
    defaultLastName = null,
    onRepairStart = null,
    onRepairComplete = null,
    onRepairError = null
  } = options;

  try {
    // First, detect if user is orphaned
    const isOrphaned = await detectOrphanedUser(firebaseUser);
    
    if (!isOrphaned) {
      console.log('User is not orphaned, no repair needed');
      return null;
    }

    console.log('Orphaned user detected, attempting repair...');
    
    if (onRepairStart) {
      onRepairStart();
    }

    // Get names for repair
    let firstName = defaultFirstName;
    let lastName = defaultLastName;

    if (!firstName || !lastName) {
      const extractedNames = extractNamesFromFirebaseUser(firebaseUser);
      firstName = firstName || extractedNames.firstName;
      lastName = lastName || extractedNames.lastName;
    }

    // If we still don't have names and prompting is enabled, we'd need to show a modal
    // For now, use extracted names or defaults
    if (!firstName || !lastName) {
      if (promptForNames) {
        console.warn('Names required for repair but prompting not implemented yet');
        // TODO: Implement name prompting modal
      }
      firstName = firstName || 'User';
      lastName = lastName || 'Account';
    }

    // Attempt repair
    const repairedData = await repairOrphanedUser(firebaseUser, firstName, lastName);
    
    // Update user state if provided
    if (setUser && repairedData) {
      const mergedUser = {
        ...firebaseUser,
        ...repairedData,
        profileLastFetched: Date.now()
      };
      setUser(mergedUser);
    }

    if (onRepairComplete) {
      onRepairComplete(repairedData);
    }

    console.log('Orphaned user repair completed successfully');
    return repairedData;

  } catch (error) {
    console.error('Error in orphaned user detection/repair workflow:', error);
    
    if (onRepairError) {
      onRepairError(error);
    }
    
    throw error;
  }
};

/**
 * Checks if the current Firebase Auth user is orphaned and attempts repair
 * Convenience function for use in auth contexts
 * 
 * @param {Function} setUser - Function to update user state
 * @param {Object} options - Options for repair process
 * @returns {Promise<Object|null>} Repaired user data or null if not orphaned
 */
export const checkAndRepairCurrentUser = async (setUser, options = {}) => {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    console.log('No current user to check for orphaned state');
    return null;
  }

  return await detectAndRepairOrphanedUser(currentUser, setUser, options);
}; 